import { apiClient, type AdminUserTokenUsage } from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { useFetch } from '@/hooks/useFetch';
import { formatTokenCount, formatUsd } from '@/lib/format';

function formatUtcDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

function DailyTokenBarChart({
  days
}: {
  days: AdminUserTokenUsage['per_day'];
}) {
  const max = Math.max(...days.map((d) => d.total_tokens), 1);
  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex h-24 items-end gap-px">
        {days.map((day) => (
          <Tooltip key={day.day}>
            <TooltipTrigger asChild>
              <div className="flex h-full flex-1 items-end">
                <div
                  className="w-full rounded-t bg-primary/70 hover:bg-primary"
                  style={{
                    height: `${Math.max((day.total_tokens / max) * 100, day.total_tokens ? 3 : 1)}%`
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="font-mono tabular-nums text-xs">
              {formatUtcDay(day.day)} · {formatTokenCount(day.total_tokens)} tok
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}

function PerModelTable({
  rows
}: {
  rows: AdminUserTokenUsage['per_model_lifetime'];
}) {
  if (!rows.length) {
    return (
      <div className="text-sm text-muted-foreground">No model usage recorded.</div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Model</TableHead>
          <TableHead className="text-right">Input</TableHead>
          <TableHead className="text-right">Output</TableHead>
          <TableHead className="text-right">Cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.model}|${row.provider}`}>
            <TableCell className="max-w-[160px]">
              <div className="truncate font-medium">{row.model}</div>
              {row.provider ? (
                <div className="text-xs text-muted-foreground">{row.provider}</div>
              ) : null}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums">
              {formatTokenCount(row.input_tokens)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums">
              {formatTokenCount(row.output_tokens)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums">
              {row.pricing_missing ? (
                <span className="text-muted-foreground" title="pricing model missing">
                  —
                </span>
              ) : (
                formatUsd(row.cost)
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function UserTokenUsageSection({
  identifier
}: {
  identifier: string;
}) {
  const url = apiClient.buildEndpoint(
    `/admin/users/${encodeURIComponent(identifier)}/token-usage`
  );
  const { data, error, isLoading } = useFetch(url);
  const usage = data as AdminUserTokenUsage | undefined;

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  return (
    <Card className="bg-card border-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Token usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        {error || !usage?.lifetime ? (
          <div className="text-sm text-muted-foreground">
            No usage data available.
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Lifetime
              </span>
              <span className="font-mono tabular-nums text-lg font-semibold">
                {formatTokenCount(usage.lifetime.total_tokens)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground font-mono tabular-nums">
              <span>in {formatTokenCount(usage.lifetime.input_tokens)}</span>
              <span>out {formatTokenCount(usage.lifetime.output_tokens)}</span>
            </div>
            <div className="flex items-baseline justify-between border-t border-border/50 pt-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Estimated cost
              </span>
              <span className="font-mono tabular-nums text-base font-semibold">
                {formatUsd(usage.lifetime_cost)}
              </span>
            </div>
            {usage.pricing_missing ? (
              <div className="text-xs text-muted-foreground">
                Some models have no pricing — cost shown is a lower bound (pricing model missing).
              </div>
            ) : null}
            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                Last {usage.window_days} days
              </div>
              <DailyTokenBarChart days={usage.per_day ?? []} />
            </div>
            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                By model (lifetime)
              </div>
              <PerModelTable rows={usage.per_model_lifetime ?? []} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
